import React, { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAdminScope } from '../hooks/useAdminScope'
import { supabaseClient } from '../config/supabase'
import {
  BarChart3, Target, TrendingUp, Lightbulb, ArrowLeftRight,
  Filter, Loader2, Info
} from 'lucide-react'

// Tab components (to be created in Phase 3-6)
import PeoAlignmentTab from '../components/analytics/PeoAlignmentTab'
import TracerStudyTab from '../components/analytics/TracerStudyTab'
import ComparativeTab from '../components/analytics/ComparativeTab'
import InsightsTab from '../components/analytics/InsightsTab'

// ── Types ─────────────────────────────────────────────────────
type AnalyticsTab = 'peo' | 'tracer' | 'comparative' | 'interventions'

interface TabDef {
  key: AnalyticsTab
  label: string
  icon: React.ReactNode
  superAdminOnly?: boolean
}

// ── Component ─────────────────────────────────────────────────
const AnalyticsPage: React.FC = () => {
  const { isSuperAdmin, department, allowedDepartments, loading: scopeLoading } = useAdminScope()
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = (searchParams.get('tab') as AnalyticsTab | null) ?? 'peo'

  // Filters
  const [departmentFilter, setDepartmentFilter] = useState<string | null>(null)
  const [programFilter, setProgramFilter] = useState<string | null>(null)
  const [batchYearFilter, setBatchYearFilter] = useState<number | null>(null)

  // Dropdown data
  const [departments, setDepartments] = useState<{ name: string }[]>([])
  const [programs, setPrograms] = useState<{ code: string; name: string; department: string }[]>([])
  const [batchYears, setBatchYears] = useState<number[]>([])

  // For dept admins, always use their fixed department; superadmins use the filter dropdown
  const effectiveDeptFilter = isSuperAdmin ? departmentFilter : (department ?? null)

  // Fetch dropdown data
  useEffect(() => {
    const fetchFilters = async () => {
      // Departments
      const { data: deptData } = await supabaseClient
        .from('departments')
        .select('name')
        .eq('is_active', true)
        .order('name')
      if (deptData) setDepartments(deptData)

      // Programs
      const { data: progData } = await supabaseClient
        .from('programs')
        .select('code, name, full_name, departments!inner(name)')
        .eq('is_active', true)
        .order('code')
      if (progData) {
        setPrograms(progData.map((p: any) => ({
          code: p.code,
          name: p.full_name || p.name,
          department: p.departments?.name || '',
        })))
      }

      // Batch years from alumni
      const { data: yearData } = await supabaseClient
        .from('alumni_profiles')
        .select('year_graduated')
        .eq('approval_status', 'approved')
        .not('year_graduated', 'is', null)
        .order('year_graduated', { ascending: false })
      if (yearData) {
        const unique = [...new Set(yearData.map(r => r.year_graduated).filter(Boolean))]
        setBatchYears(unique as number[])
      }
    }
    fetchFilters()
  }, [])

  // Filter programs by selected department
  const filteredPrograms = useMemo(() => {
    if (!effectiveDeptFilter) return programs
    return programs.filter(p => p.department === effectiveDeptFilter)
  }, [programs, effectiveDeptFilter])

  // Filter departments for non-superadmins
  const availableDepartments = useMemo(() => {
    if (isSuperAdmin) return departments
    if (allowedDepartments) return departments.filter(d => allowedDepartments.includes(d.name))
    return []
  }, [departments, isSuperAdmin, allowedDepartments])

  // Tabs definition
  const tabs: TabDef[] = [
    { key: 'peo', label: 'PEO/PO Alignment', icon: <Target className="w-4 h-4" /> },
    { key: 'tracer', label: 'Tracer Study', icon: <TrendingUp className="w-4 h-4" /> },
    { key: 'comparative', label: 'Comparative', icon: <ArrowLeftRight className="w-4 h-4" /> },
    { key: 'interventions', label: 'Insights', icon: <Lightbulb className="w-4 h-4" /> },
  ]

  const visibleTabs = tabs.filter(t => !t.superAdminOnly || isSuperAdmin)

  if (scopeLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-blue-900" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-blue-900 rounded-2xl p-6 sm:p-8 text-white relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-52 h-52 bg-white/5 rounded-full pointer-events-none" />
        <div className="absolute -bottom-8 right-16 w-40 h-40 bg-yellow-400/10 rounded-full pointer-events-none" />
        <div className="absolute top-4 right-32 w-16 h-16 bg-white/5 rounded-full pointer-events-none" />
        <div className="relative flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
            <BarChart3 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">Analytics & Decision Support</h1>
            <p className="text-blue-200 text-sm mt-1">
              {isSuperAdmin
                ? 'University-wide analytics, PEO alignment, and tracer study insights'
                : `Analytics for ${department || 'your department'}`
              }
            </p>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-gray-400" />
          <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Filters</span>
          {activeTab === 'comparative' ? (
            <span className="ml-auto flex items-center gap-1 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5">
              <Info className="w-3 h-3" />
              Department Comparison ignores these filters — use Program Comparison to scope by department
            </span>
          ) : (
            <span className="ml-auto text-xs text-gray-400">
              Applies to: <span className="font-medium text-gray-500">
                {activeTab === 'peo' && 'PEO/PO scores and heatmap'}
                {activeTab === 'tracer' && 'Employment, salary, and trend data'}
                {activeTab === 'interventions' && 'Department scope for insights analysis'}
              </span>
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-3">
          {/* Department filter (superadmin only shows multiple, dept admin scoped) */}
          {isSuperAdmin && (
            <select
              value={departmentFilter || ''}
              onChange={e => {
                setDepartmentFilter(e.target.value || null)
                setProgramFilter(null)
              }}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 min-w-[180px]"
            >
              <option value="">All Departments</option>
              {availableDepartments.map(d => (
                <option key={d.name} value={d.name}>{d.name}</option>
              ))}
            </select>
          )}

          {/* Program filter */}
          <select
            value={programFilter || ''}
            onChange={e => setProgramFilter(e.target.value || null)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 min-w-[180px]"
          >
            <option value="">All Programs</option>
            {filteredPrograms.map(p => (
              <option key={p.code} value={p.code}>{p.code}</option>
            ))}
          </select>

          {/* Batch year filter */}
          <select
            value={batchYearFilter || ''}
            onChange={e => setBatchYearFilter(e.target.value ? Number(e.target.value) : null)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 min-w-[140px]"
          >
            <option value="">All Batch Years</option>
            {batchYears.map(y => (
              <option key={y} value={y}>Class of {y}</option>
            ))}
          </select>

          {/* Clear */}
          {(departmentFilter || programFilter || batchYearFilter) && (
            <button
              onClick={() => {
                if (isSuperAdmin) setDepartmentFilter(null)
                setProgramFilter(null)
                setBatchYearFilter(null)
              }}
              className="text-sm text-gray-500 hover:text-gray-700 px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-1 border-b border-gray-200">
        {visibleTabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setSearchParams({ tab: tab.key })}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 ${
              activeTab === tab.key
                ? 'text-blue-900 border-blue-900'
                : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'peo' && (
        <PeoAlignmentTab
          department={effectiveDeptFilter}
          program={programFilter}
          batchYear={batchYearFilter}
        />
      )}
      {activeTab === 'tracer' && (
        <TracerStudyTab
          department={effectiveDeptFilter}
          program={programFilter}
          batchYear={batchYearFilter}
        />
      )}
      {activeTab === 'comparative' && (
        <ComparativeTab
          department={effectiveDeptFilter}
          isSuperAdmin={isSuperAdmin}
          programs={filteredPrograms}
        />
      )}
      {activeTab === 'interventions' && (
        <InsightsTab
          department={effectiveDeptFilter}
          program={programFilter}
          batchYear={batchYearFilter}
        />
      )}
    </div>
  )
}

export default AnalyticsPage
